import { StoreTapStatsResponse } from '../../types/marketing';
import CampaignBreakdownTable from './CampaignBreakdownTable';
import DailyTapsChart from './DailyTapsChart';
import StoreTapKpis from './StoreTapKpis';

export type StoreTapsState =
  | { status: 'loading' }
  | { status: 'ok'; data: StoreTapStatsResponse }
  | { status: 'unavailable' }
  | { status: 'forbidden' }
  | { status: 'error'; message: string };

export function MarketingSkeleton() {
  return (
    <div className="mkt-skeleton" aria-busy="true" aria-label="Loading numbers" data-testid="marketing-skeleton">
      <div className="mkt-kpis">{[0, 1, 2].map((i) => <div key={i} className="mkt-kpi"><div className="bs-skel mkt-skel-line" /><div className="bs-skel mkt-skel-big" /><div className="bs-skel mkt-skel-line" /></div>)}</div>
      <div className="mkt-card"><div className="bs-skel mkt-skel-chart" /></div>
      <div className="mkt-card"><div className="bs-skel mkt-skel-line" /><div className="bs-skel mkt-skel-line" /><div className="bs-skel mkt-skel-line" /></div>
    </div>
  );
}

/** The page-level 503 / error card ("Numbers unavailable, try again" + Retry). */
export function MarketingError({ unavailable, message, onRetry }: { unavailable: boolean; message?: string; onRetry: () => void }) {
  return (
    <div className="dash-block-error" role="alert" data-testid="marketing-error">
      <div>
        <div className="dash-block-error__title">{unavailable ? 'Numbers unavailable, try again' : "Couldn't load the numbers"}</div>
        {!unavailable && message && <div className="dash-block-error__msg">{message}</div>}
      </div>
      <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={onRetry}>Retry</button>
    </div>
  );
}

export function MarketingForbidden() {
  return (
    <div className="mkt-page"><div className="mkt-card" role="alert" data-testid="marketing-forbidden">
      <h2 className="dash-v2__title">You don't have access</h2>
      <p className="mkt-foot">Marketing numbers are for admins holding the Marketing screen. Ask a super admin to add it to your role.</p>
    </div></div>
  );
}

/** Spec 078 view (KPIs, taps per day, breakdown): the whole page when the insights switch is off (404 FEATURE_OFF). */
export default function StoreTapsView({ state, onRetry }: { state: StoreTapsState; onRetry: () => void }) {
  if (state.status === 'loading') return <MarketingSkeleton />;
  if (state.status === 'unavailable' || state.status === 'error') {
    return <MarketingError unavailable={state.status === 'unavailable'} message={state.status === 'error' ? state.message : undefined} onRetry={onRetry} />;
  }
  if (state.status !== 'ok') return null;
  return (
    <div data-testid="store-taps-view">
      <StoreTapKpis stats={state.data} />
      <section className="mkt-card"><DailyTapsChart daily={state.data.daily} /></section>
      <CampaignBreakdownTable byCampaign={state.data.byCampaign} byPage={state.data.byPage} from={state.data.from} to={state.data.to} />
      <p className="mkt-foot">Days are Dubai days. Counted on our servers when a visitor taps App Store or Google Play on a share page.</p>
    </div>
  );
}
